import React, { useContext, useMemo, useState } from 'react'
import axios from 'axios'
import { format } from 'date-fns'

import { Field, FieldGroup, Form } from 'Components/form'
import { Button, ModalActionsFooter } from 'Components/readonly'
import { AuthContext, GrowlContext } from 'Context'

import { injectIntl } from 'react-intl'
import { Datepicker, Tree } from 'Components/input'
import { transformLocations } from 'Utils/location'
import useGetData from 'Hooks/useGetData'
import { InventoryStartLockMessage } from './inventoryStartLockMessage'
import ResponsibleUserDropdown from './responsibleUserDropdown'

const InventoryDialog = ({ modalController, updateList, createList, intl }) => {
  const [isLoading, setIsLoading] = useState(false)
  const { displayError } = useContext(GrowlContext)

  const inventoryData = modalController.data

  // inventoryData is null when creating, and contains data when editing

  const isEditMode = !!inventoryData

  // Dates - We use an array internally and for posting, but never starts with one from backend
  const [dates, setDates] = useState(() => {
    if (isEditMode) {
      if (inventoryData.dates) {
        return inventoryData.dates.map((d) => ({
          date: new Date(d.date),
          annually: d.annually
        }))
      }
      return [{
        date: new Date(inventoryData.start_date),
        annually: inventoryData.annually
      }]
    }
    return [{
      date: new Date(),
      annually: false
    }]
  })

  const addDate = () => {
    setDates([...dates, { date: new Date(), annually: false }])
  }

  const removeDate = (index) => {
    setDates(dates.filter((_, i) => i !== index))
  }

  const updateDate = (index, newValue) => {
    const updated = [...dates]
    updated[index].date = newValue
    setDates(updated)
  }

  const updateAnnually = (index, checked) => {
    const updated = [...dates]
    updated[index].annually = checked
    setDates(updated)
  }

  // Responsible users
  const { organisation } = useContext(AuthContext)
  const [responsibleListLoading, responsibleList] = useGetData(
    '/rapi/inventorying/responsibleusers',
    null,
    [organisation]
  )

  const responsibleOptions = useMemo(() => {
    const responsibleOptions = [{ label: intl.formatMessage({ id: 'inventory.select_responsible' }), value: null }]
    responsibleList &&
      responsibleList.forEach((user) => {
        responsibleOptions.push({
          label: `${user.name} (${user.email})`,
          value: user.id,
          limitLocations: user.limitLocations,
        })
      })
    return responsibleOptions
  }, [responsibleList, intl])

  // Locations
  const [selectedLocations, setSelectedLocations] = useState(() => {
    return {
      selectedKeys:
        (inventoryData &&
          inventoryData.locations &&
          inventoryData.locations.map((location) => parseInt(location.location_id))) ||
        [],
      expandedKeys: []
    }
  })

  const [locationsLoading, locations] = useGetData('/rapi/locations', null, [organisation])

  // Submit
  const onSubmit = async (formData) => {
    setIsLoading(true)

    const payload = {
      ...formData,
      dates: dates.map((d) => ({
        date: format(d.date, 'YYYY-MM-DD'),
        annually: d.annually
      }))
    }

    try {
      let response
      if (isEditMode) {
        response = await axios.put('/rapi/inventorying/' + inventoryData.id, payload)
        updateList(response.data)
      } else {
        response = await axios.post('/rapi/inventorying', payload)
        createList(response.data)
      }

      setIsLoading(false)
      modalController.close()
    } catch (error) {
      displayError(error)
      setIsLoading(false)
    }
  }

  return (
    <Form onSubmit={onSubmit}>
      <InventoryStartLockMessage />
      <FieldGroup>
        <div style={{ marginBottom: '1rem' }}>
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: 8 }}>
            {intl.formatMessage({ id: 'common.date' })} {isEditMode ?? 'Uppdatera'}
          </label>

          {dates.map((item, index) => (
            <div
              key={index}
              style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}
            >
              <Datepicker
                dateFormat="yy-mm-dd"
                minDate={new Date()}
                label={intl.formatMessage({ id: 'common.date' })}
                value={item.date}
                onChange={(e) => updateDate(index, e.value)}
                required
                style={{ marginRight: 12 }}
              />

              <label style={{ display: 'flex', alignItems: 'center', marginRight: 12 }}>
                <input
                  type="checkbox"
                  checked={item.annually}
                  onChange={(e) => updateAnnually(index, e.target.checked)}
                  style={{ marginRight: 6 }}
                />
                {intl.formatMessage({ id: 'inventory.annually' })}
              </label>

              {index > 0 && (
                <Button
                  icon="pi pi-trash"
                  className="p-button-danger"
                  onClick={() => removeDate(index)}
                />
              )}
            </div>
          ))}

          <Button
            primary
            icon="pi pi-plus"
            label={intl.formatMessage({ id: 'inventory.more_dates' })}
            onClick={addDate}
          />
        </div>

        <Field
          id="location_ids"
          Component={Tree}
          appendToBody={true}
          label={intl.formatMessage({ id: 'inventory.choose_location' })}
          required={true}
          isLoading={locationsLoading}
          options={transformLocations(locationsLoading ? [] : locations)}
          initialValue={selectedLocations.selectedKeys}
          multiselectAll={true}
          expandedKeys={selectedLocations.expandedKeys}
          multiselect={true}
        />

        <Field
          id="responsible_user_id"
          label={intl.formatMessage({ id: 'common.responsible' })}
          Component={ResponsibleUserDropdown}
          options={responsibleOptions}
          isLoading={responsibleListLoading}
          disabled={responsibleListLoading}
          appendTo={document.body}
          initialValue={isEditMode ? parseInt(inventoryData.responsible_user_id) : null}
          required={true}
        />
      </FieldGroup>

      <ModalActionsFooter>
        <Button label={intl.formatMessage({ id: 'common.cancel' })} onClick={() => modalController.close()} />
        <Button
          id="inventorying-schedule-button"
          primary
          submit
          icon={isEditMode ? 'pi pi-save' : 'pi pi-arrow-circle-right'}
          label={isEditMode ? intl.formatMessage({ id: 'common.save' }) : intl.formatMessage({ id: 'common.schedule' })}
          isLoading={isLoading}
        />
      </ModalActionsFooter>
    </Form>
  )
}

export default injectIntl(InventoryDialog)
